import {
  Box,
  VStack,
  Text,
  Checkbox,
  CheckboxGroup,
  Flex,
  Button,
} from "@chakra-ui/react";
import type { Product } from "../types";

export interface FilterState {
  brands: string[];
  availability: string[];
  specs: Record<string, string[]>;
}

export const EMPTY_FILTER_STATE: FilterState = {
  brands: [],
  availability: [],
  specs: {},
};

interface FilterSidebarProps {
  products: Product[];
  filters: FilterState;
  onChange: (filters: FilterState) => void;
}

const countBy = <T,>(values: T[]) => {
  const counts = new Map<T, number>();
  values.forEach((v) => counts.set(v, (counts.get(v) ?? 0) + 1));
  return counts;
};

const FilterSidebar = ({ products, filters, onChange }: FilterSidebarProps) => {
  const brandCounts = countBy(
    products.map((p) => p.brand).filter((b): b is string => Boolean(b)),
  );
  const availabilityCounts = countBy(
    products
      .map((p) => p.availability)
      .filter((a): a is NonNullable<typeof a> => Boolean(a)),
  );

  const specKeys = Array.from(
    new Set(
      products.flatMap((p) => (p.specs ? Object.keys(p.specs) : [])),
    ),
  );

  const hasAnyFilters =
    filters.brands.length > 0 ||
    filters.availability.length > 0 ||
    Object.values(filters.specs).some((v) => v.length > 0);

  const specValueCounts = (key: string) =>
    countBy(
      products
        .map((p) => p.specs?.[key])
        .filter((v): v is string => Boolean(v)),
    );

  return (
    <Box
      w={{ base: "100%", md: "240px" }}
      flexShrink={0}
      bg="rs.surface"
      border="1px solid"
      borderColor="rs.border"
      borderRadius="10px"
      p={5}
    >
      <Flex align="center" justify="space-between" mb={4}>
        <Text fontSize="12px" fontWeight="600" letterSpacing="0.08em" textTransform="uppercase" color="rs.text">
          Filters
        </Text>
        {hasAnyFilters && (
          <Button
            variant="ghost"
            size="xs"
            color="rs.red"
            onClick={() => onChange(EMPTY_FILTER_STATE)}
          >
            Clear all
          </Button>
        )}
      </Flex>

      {brandCounts.size > 0 && (
        <Box mb={5}>
          <Text fontSize="11px" fontWeight="600" color="rs.textMuted" mb={2}>
            Brand
          </Text>
          <CheckboxGroup
            value={filters.brands}
            onChange={(vals) =>
              onChange({ ...filters, brands: vals as string[] })
            }
          >
            <VStack align="flex-start" spacing={2}>
              {Array.from(brandCounts.entries()).map(([brand, count]) => (
                <Checkbox key={brand} value={brand} fontSize="13px" colorScheme="red">
                  {brand} <Text as="span" color="rs.textDim">({count})</Text>
                </Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
        </Box>
      )}

      {availabilityCounts.size > 0 && (
        <Box mb={5}>
          <Text fontSize="11px" fontWeight="600" color="rs.textMuted" mb={2}>
            Availability
          </Text>
          <CheckboxGroup
            value={filters.availability}
            onChange={(vals) =>
              onChange({ ...filters, availability: vals as string[] })
            }
          >
            <VStack align="flex-start" spacing={2}>
              {Array.from(availabilityCounts.entries()).map(([status, count]) => (
                <Checkbox key={status} value={status} fontSize="13px" colorScheme="red">
                  {status} <Text as="span" color="rs.textDim">({count})</Text>
                </Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
        </Box>
      )}

      {specKeys.map((key) => {
        const counts = specValueCounts(key);
        if (counts.size === 0) return null;
        return (
          <Box key={key} mb={5}>
            <Text fontSize="11px" fontWeight="600" color="rs.textMuted" mb={2} textTransform="capitalize">
              {key}
            </Text>
            <CheckboxGroup
              value={filters.specs[key] ?? []}
              onChange={(vals) =>
                onChange({
                  ...filters,
                  specs: { ...filters.specs, [key]: vals as string[] },
                })
              }
            >
              <VStack align="flex-start" spacing={2}>
                {Array.from(counts.entries()).map(([value, count]) => (
                  <Checkbox key={value} value={value} fontSize="13px" colorScheme="red">
                    {value} <Text as="span" color="rs.textDim">({count})</Text>
                  </Checkbox>
                ))}
              </VStack>
            </CheckboxGroup>
          </Box>
        );
      })}
    </Box>
  );
};

export const applyFilters = (products: Product[], filters: FilterState) =>
  products.filter((p) => {
    if (filters.brands.length > 0 && !filters.brands.includes(p.brand ?? "")) {
      return false;
    }
    if (
      filters.availability.length > 0 &&
      !filters.availability.includes(p.availability ?? "")
    ) {
      return false;
    }
    for (const [key, values] of Object.entries(filters.specs)) {
      if (values.length > 0 && !values.includes(p.specs?.[key] ?? "")) {
        return false;
      }
    }
    return true;
  });

export default FilterSidebar;
